/**
 * Which surface started an OAuth sign-in, carried across the provider round
 * trip so the server-fired `signup_completed` / `login_completed` in
 * app/auth/callback/route.ts can name it.
 *
 * Written client-side by SocialLoginButtons immediately before the redirect,
 * read and cleared by the callback on every outcome. Same channel and the same
 * rules as `ph_oauth_identity` (lib/analytics.ts): ANALYTICS ONLY. The cookie is
 * JS-written, not HttpOnly and forgeable, so it must never gate auth, quota or
 * entitlement. It holds a surface label and the opaque scan ids, never personal
 * data, and nothing from it is added to a URL.
 *
 * No browser or posthog-js imports: the callback route imports the parser.
 */

export const OAUTH_PLACEMENT_COOKIE = 'ph_oauth_placement'
export const OAUTH_PLACEMENT_TTL_MS = 10 * 60 * 1000

const PLACEMENT_PATTERN = /^[a-z0-9_:-]{1,80}$/
const ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/

export interface OAuthPlacementStash {
  placement: string
  analysisId?: string
  funnelId?: string
}

function cleanId(value: unknown): string | undefined {
  return typeof value === 'string' && ID_PATTERN.test(value) ? value : undefined
}

export function isValidOAuthPlacement(value: unknown): value is string {
  return typeof value === 'string' && PLACEMENT_PATTERN.test(value)
}

export function stashOAuthPlacement(stash: OAuthPlacementStash, now = Date.now()): void {
  try {
    if (typeof document === 'undefined') return
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    if (!isValidOAuthPlacement(stash.placement)) {
      // Never leave an earlier redirect's placement to attribute this one.
      document.cookie = `${OAUTH_PLACEMENT_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`
      return
    }
    const payload = encodeURIComponent(
      JSON.stringify({
        p: stash.placement,
        aid: cleanId(stash.analysisId),
        fid: cleanId(stash.funnelId),
        ts: now,
      }),
    )
    document.cookie =
      `${OAUTH_PLACEMENT_COOKIE}=${payload}` +
      `; path=/; max-age=${Math.floor(OAUTH_PLACEMENT_TTL_MS / 1000)}; SameSite=Lax${secure}`
  } catch {
    // Attribution is best-effort and must never block the OAuth redirect.
  }
}

export function parseOAuthPlacementCookie(
  raw: string | undefined | null,
  now = Date.now(),
): OAuthPlacementStash | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as {
      p?: unknown
      aid?: unknown
      fid?: unknown
      ts?: unknown
    }
    const age = typeof parsed.ts === 'number' && Number.isFinite(parsed.ts)
      ? now - parsed.ts
      : Number.NaN
    // 2-minute skew allowance on both sides, as for the other OAuth stashes.
    if (!(age >= -2 * 60 * 1000 && age <= OAUTH_PLACEMENT_TTL_MS + 2 * 60 * 1000)) return null
    if (!isValidOAuthPlacement(parsed.p)) return null
    return {
      placement: parsed.p,
      analysisId: cleanId(parsed.aid),
      funnelId: cleanId(parsed.fid),
    }
  } catch {
    return null
  }
}
